import type { ODataQueryOptions } from '@experiences/interfaces';
import {
    getOrchestratorApiRoute,
    getOrchestratorODataRoute,
} from '@experiences/util';
import buildQuery from 'odata-query';

import {
    axiosDelete,
    get,
    post,
} from '../utility/Requests';
import type { Role } from './OrchRoleAssignmentService';

export enum FolderType {
    Standard = 'Standard',
    Personal = 'Personal',
    Virtual = 'Virtual',
    Solution = 'Solution',
}

export enum FeedType {
    Undefined = 'Undefined',
    Processes = 'Processes',
    Libraries = 'Libraries',
    PersonalWorkspace = 'PersonalWorkspace',
    FolderHierarchy = 'FolderHierarchy',
}

export type NewFolder = {
    DisplayName: string;
    FolderType: FolderType;
    FeedType: FeedType;
};

export type Folder = NewFolder & {
    IsSelectable?: boolean;
    HasChildren?: boolean;
    Level?: number;
    Key: string;
    FullyQualifiedName: string;
    Description?: string;
    IsPersonal?: boolean;
    ProvisionType: 'Manual' | 'Automatic';
    PermissionModel: 'InheritFromTenant' | 'FineGrained';
    ParentId?: number;
    ParentKey?: string;
    Id: number;
};

type FolderDto = {
    '@odata.count': number;
    value: Folder[];
};

export type CurrentUserFolderDto = {
    Count: number;
    PageItems: Folder[];
};

export type UserEntity = {
    UserName: string;
    IsInherited: boolean;
    MayHaveAttended: boolean;
    MayHaveUnattended: boolean;
    Type: string;
    Source: string;
    Id: number;
    FullName: string;
    AuthenticationSource: string;
};

export type UserRole = {
    HasAlertsEnabled: boolean;
    Id: number;
    UserEntity: UserEntity;
    Roles: Role[];
};

export type UserRolesResponse = {
    '@odata.context': string;
    '@odata.count': number;
    value: UserRole[];
};

export interface MachineToFolderAssociation {
    associations: {
        FolderId: number;
        AddedMachineIds: number[];
        RemovedMachineIds: number[];
    };
}

export async function getAllFolders(
    urlParams: { accountLogicalName: string; tenantName: string },
    queryOptions: ODataQueryOptions
) {
    const {
        accountLogicalName, tenantName,
    } = urlParams;
    const url = `${getOrchestratorODataRoute(accountLogicalName, tenantName)}/Folders${buildQuery(queryOptions)}`;
    return await get<FolderDto>(url, { ignoreBasePath: true });
}

export interface GetFoldersForCurrentUserParams {
    searchText?: string;
    folderTypes?: FolderType[];
    skip?: number;
    take?: number;
}

export async function getFoldersForCurrentUser(
    urlParams: { accountLogicalName: string; tenantName: string },
    options?: GetFoldersForCurrentUserParams
): Promise<CurrentUserFolderDto> {
    const { tenantName } = urlParams;
    const queryParams = new URLSearchParams();

    if (options?.skip) {
        queryParams.append('skip', String(options.skip));
    }
    if (options?.take) {
        queryParams.append('take', String(options.take));
    }
    if (options?.searchText) {
        queryParams.append('searchText', options.searchText);
    }
    options?.folderTypes?.forEach(type => queryParams.append('folderTypes', type));

    const queryString = queryParams.toString();
    const url = `/${tenantName}/orchestrator_/api/FoldersNavigation/GetFoldersForCurrentUser${queryString ? `?${queryString}` : ''}`;
    return get<CurrentUserFolderDto>(url, { stripPortalPath: true });
}

export async function getAllFoldersForCurrentUser(urlParams: { accountLogicalName: string; tenantName: string }) {
    const {
        accountLogicalName, tenantName,
    } = urlParams;
    const url = `${getOrchestratorApiRoute(accountLogicalName, tenantName)}/Folders/GetAllForCurrentUser`;
    return await get<CurrentUserFolderDto>(url, { ignoreBasePath: true });
}

export async function getAllFoldersForCurrentUserV2(tenantName: string) {
    const url = `/${tenantName}/orchestrator_/api/FoldersNavigation/GetAllFoldersForCurrentUser`;
    return get<Folder[]>(url, { stripPortalPath: true }).then((data) => data);
}

export async function getFolder(urlParams: { accountLogicalName: string; tenantName: string }, folderKey: string) {
    const {
        accountLogicalName, tenantName,
    } = urlParams;
    const url = `${getOrchestratorODataRoute(accountLogicalName, tenantName)}/Folders(${folderKey})`;
    return await get<Folder>(url, { ignoreBasePath: true });
}

export async function getFolderByGuidKey(
    urlParams: { accountLogicalName: string; tenantName: string },
    folderGuidKey: string,
): Promise<Folder | undefined> {
    const {
        accountLogicalName, tenantName,
    } = urlParams;
    const url = `${getOrchestratorODataRoute(accountLogicalName, tenantName)}/Folders?$filter=Key eq ${folderGuidKey}&$top=1`;
    const result = await get<FolderDto>(url, { ignoreBasePath: true });
    return result?.value?.[0];
}

export async function createFolder(urlParams: { accountLogicalName: string; tenantName: string }, folder: NewFolder) {
    const {
        accountLogicalName, tenantName,
    } = urlParams;
    const url = `${getOrchestratorODataRoute(accountLogicalName, tenantName)}/Folders`;
    return await post<Folder>(url, {
        body: folder,
        ignoreBasePath: true,
    });
}

export async function deleteFolder(urlParams: { accountLogicalName: string; tenantName: string }, folderKey: string) {
    const {
        accountLogicalName, tenantName,
    } = urlParams;
    const url = `${getOrchestratorODataRoute(accountLogicalName, tenantName)}/Folders(${folderKey})`;
    return await axiosDelete(url, { ignoreBasePath: true });
}

export async function getOrchUsersForFolder(body: { folderId: number; tenantName: string; organizationName: string }) {
    const {
        folderId, tenantName, organizationName,
    } = body;
    const baseUrl = getOrchestratorODataRoute(organizationName, tenantName);
    const url = `${baseUrl}/Folders/UiPath.Server.Configuration.OData.GetUsersForFolder(key=${folderId},includeInherited=true)?$top=25&includeAlertsEnabled=true`;
    return await get<UserRolesResponse>(url, { ignoreBasePath: true });
}

export async function updateMachinesToFolderAssociations(organizationName: string, tenantName: string, folderId: string, body: MachineToFolderAssociation) {
    const url = `${getOrchestratorODataRoute(organizationName, tenantName)}/Folders/UiPath.Server.Configuration.OData.UpdateMachinesToFolderAssociations`;
    return await post(url, {
        body,
        ignoreBasePath: true,
        headers: {
            'x-uipath-organizationunitid': folderId,
            extendRequestHeaders: 'true',
        },
    });
}
